import { useState, useMemo, useCallback } from 'react';

/**
 * Custom hook for handling product pagination with "Load More" pattern
 * @param {Array} products - Full array of products
 * @param {number} initialCount - Number of products to show initially (default: 12)
 * @param {number} loadMoreCount - Number of products to load on each "Load More" click (default: 8)
 * @returns {Object} Pagination state and methods
 */
export const useProductPagination = (
  products = [],
  initialCount = 12,
  loadMoreCount = 8
) => {
  const [displayedCount, setDisplayedCount] = useState(initialCount);

  // Memoize visible products to avoid unnecessary recalculations
  const visibleProducts = useMemo(() => {
    return products.slice(0, displayedCount);
  }, [products, displayedCount]);

  // Calculate if there are more products to load
  const hasMore = useMemo(() => {
    return displayedCount < products.length;
  }, [displayedCount, products.length]);

  // Calculate remaining products count
  const remainingCount = useMemo(() => {
    return Math.max(0, products.length - displayedCount);
  }, [products.length, displayedCount]);

  // Calculate how many products will be loaded on next click
  const nextLoadCount = useMemo(() => {
    return Math.min(loadMoreCount, remainingCount);
  }, [loadMoreCount, remainingCount]);

  // Load more products handler
  const loadMore = useCallback(() => {
    setDisplayedCount((prev) => Math.min(prev + loadMoreCount, products.length));
  }, [loadMoreCount, products.length]);

  // Reset to initial count (useful for filters in Phase 2)
  const reset = useCallback(() => {
    setDisplayedCount(initialCount);
  }, [initialCount]);

  return {
    visibleProducts,
    hasMore,
    remainingCount,
    nextLoadCount,
    displayedCount,
    totalCount: products.length,
    loadMore,
    reset,
  };
};
